// Gemini Service - Routes through Supabase Edge Functions
// v2: Uses execute-agent orchestrator for all AI calls
// This enables table-driven model configuration without code changes

import {
  Language,
  LanguageCode,
  JournalistAgentResponse,
  TvJournalistAgentResponse,
  CriticAgentResponse,
  SocialAgentResponse,
  EditorIssue,
  ReviewMode,
  BCP47_TO_LEGACY,
  DurationBreakdown,
  TvWordTargets,
} from '../types';
import { supabase } from './supabaseClient';

// Re-export types for backwards compatibility
export type JournalistResponse = JournalistAgentResponse;
export type CriticResponse = CriticAgentResponse;
export type SocialPosts = SocialAgentResponse;

// Get the Supabase URL for Edge Functions
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// Error result type for better error handling
export interface AgentError {
  error: true;
  message: string;
  details?: string;
  code?: string | number;
}

/**
 * Optional context for agents that can store data (like journalist_tv storing facts)
 * Pass this when calling agents that need to link data back to a story
 */
export interface StoryContext {
  storyId: string;
  groupId: string;
}

// NOTE: We intentionally do NOT cache sessions here.
// Supabase-js handles token refresh automatically via onAuthStateChange.
// Our previous custom caching caused 401 errors when tokens were refreshed
// in the background but our cache still held the old token.

/**
 * Response from execute-agent orchestrator
 */
interface ExecuteAgentResponse<T> {
  success: boolean;
  data?: T;
  raw_text?: string;
  error?: string;
  agent_name: string;
  model: string;
  usage?: {
    input_tokens: number;
    output_tokens: number;
  };
  duration_ms: number;
}

/**
 * Response from execute-agent in dual mode
 */
interface DualAgentResponse<T> {
  success: boolean;
  dual_mode: true;
  primary: {
    success: boolean;
    data?: T;
    raw_text?: string;
    error?: string;
    agent_name: string;
    model: string;
    usage?: {
      input_tokens: number;
      output_tokens: number;
    };
  };
  validator: {
    success: boolean;
    data?: T;
    raw_text?: string;
    error?: string;
    agent_name: string;
    model: string;
    usage?: {
      input_tokens: number;
      output_tokens: number;
    };
  };
  agreement?: {
    overall: number;
    headline: number;
    deck: number;
    body: number;
  } | null;
  duration_ms: number;
}

/**
 * Result from dual polish operation
 */
export interface DualPolishResult {
  primary: {
    success: boolean;
    headline?: string;
    deck?: string;
    body?: string;
    error?: string;
    model: string;
  };
  validator: {
    success: boolean;
    headline?: string;
    deck?: string;
    body?: string;
    error?: string;
    model: string;
  };
  agreement: {
    overall: number;      // 0-100 percentage
    headline: number;
    deck: number;
    body: number;
    isHighConfidence: boolean;  // true if overall >= 90%
  };
  duration_ms: number;
}

/**
 * Get the current valid session for authenticated API calls.
 *
 * IMPORTANT: Always call supabase.auth.getSession() fresh - never cache.
 * Supabase-js handles token refresh automatically. Our previous custom
 * caching caused 401 errors when tokens were refreshed in the background.
 */
async function getValidSession(): Promise<{ session: any } | AgentError> {
  try {
    const { data, error } = await supabase.auth.getSession();

    if (error) {
      console.error('[Auth] Session error:', error.message);
      return {
        error: true,
        message: 'Authentication error. Please log out and log back in.',
        details: error.message,
        code: 'AUTH_ERROR'
      };
    }

    if (!data?.session) {
      console.error('[Auth] No active session');
      return {
        error: true,
        message: 'No active session. Please log in.',
        code: 'NO_SESSION'
      };
    }

    // Log expiry for debugging (Supabase handles refresh automatically)
    if (data.session.expires_at) {
      const expiresAt = new Date(data.session.expires_at * 1000);
      const timeUntilExpiry = expiresAt.getTime() - Date.now();
      console.log(`[Auth] Session valid (expires in ${Math.round(timeUntilExpiry / 1000)}s)`);
    }

    return { session: data.session };

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('[Auth] Unexpected error:', error);
    return {
      error: true,
      message: `Authentication error: ${errorMessage}`,
      code: 'AUTH_ERROR'
    };
  }
}

/**
 * Call the execute-agent orchestrator
 * This is the new unified entry point for all AI agent calls
 *
 * @param agentName - Name of the agent to execute
 * @param inputData - Input data for the agent
 * @param language - Content language (BCP47 or legacy format)
 * @param storyContext - Optional story context for fact storage (journalist_tv)
 */
async function callExecuteAgent<T>(
  agentName: string,
  inputData: Record<string, unknown>,
  language: string,
  storyContext?: StoryContext
): Promise<T | AgentError> {
  try {
    // Get valid session
    const sessionResult = await getValidSession();
    if ('error' in sessionResult) {
      return sessionResult;
    }
    const { session } = sessionResult;

    const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!SUPABASE_ANON_KEY) {
      return {
        error: true,
        message: 'Configuration error: Missing API key',
        code: 'CONFIG_ERROR'
      };
    }

    console.log(`[execute-agent] Calling agent: ${agentName}, language: ${language}${storyContext ? `, story: ${storyContext.storyId}` : ''}`);

    const response = await fetch(`${SUPABASE_URL}/functions/v1/execute-agent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        agent_name: agentName,
        input_data: inputData,
        language: language,
        // Include story context if provided (for fact storage)
        story_id: storyContext?.storyId,
        group_id: storyContext?.groupId,
      }),
    });

    if (!response.ok) {
      let errorText = '';
      try {
        errorText = await response.text();
      } catch (e) {
        errorText = `HTTP ${response.status}`;
      }

      console.error(`[execute-agent] HTTP error:`, response.status, errorText);

      return {
        error: true,
        message: `Server error: ${response.status === 401 ? 'Unauthorized' : response.status === 500 ? 'Internal server error' : `HTTP ${response.status}`}`,
        details: errorText.slice(0, 500),
        code: response.status
      };
    }

    const result: ExecuteAgentResponse<T> = await response.json();

    // Log execution info
    console.log(`[${agentName}] Model: ${result.model}, Duration: ${result.duration_ms}ms, Success: ${result.success}`);
    if (result.usage) {
      console.log(`[${agentName}] Tokens: ${result.usage.input_tokens} in / ${result.usage.output_tokens} out`);
    }

    // Check for orchestrator-level error
    if (!result.success) {
      console.error(`[execute-agent] Agent ${agentName} failed:`, result.error);
      return {
        error: true,
        message: result.error || 'Unknown error from AI agent',
        code: 'AGENT_ERROR'
      };
    }

    // Return the parsed data
    return result.data as T;

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error(`[execute-agent] Network error:`, error);

    return {
      error: true,
      message: `Network error: ${errorMessage}`,
      details: error instanceof Error ? error.stack : String(error),
      code: 'NETWORK_ERROR'
    };
  }
}

/**
 * Legacy helper to call old edge functions directly (for suggest-headline)
 * Kept for backwards compatibility with functions not yet migrated
 */
async function callEdgeFunctionLegacy<T>(functionName: string, payload: object): Promise<T | AgentError> {
  try {
    const sessionResult = await getValidSession();
    if ('error' in sessionResult) {
      return sessionResult;
    }
    const { session } = sessionResult;

    const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!SUPABASE_ANON_KEY) {
      return {
        error: true,
        message: 'Configuration error: Missing API key',
        code: 'CONFIG_ERROR'
      };
    }

    console.log(`[EdgeFunction] Calling ${functionName} (legacy)`);

    const response = await fetch(`${SUPABASE_URL}/functions/v1/${functionName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorText = '';
      try {
        errorText = await response.text();
      } catch (e) {
        errorText = `HTTP ${response.status}`;
      }

      return {
        error: true,
        message: `Server error: HTTP ${response.status}`,
        details: errorText.slice(0, 500),
        code: response.status
      };
    }

    const data = await response.json();

    if (data.error) {
      return {
        error: true,
        message: data.error || 'Unknown error from edge function',
        code: 'FUNCTION_ERROR'
      };
    }

    return data as T;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return {
      error: true,
      message: `Network error: ${errorMessage}`,
      code: 'NETWORK_ERROR'
    };
  }
}

/**
 * Convert language to string format for the orchestrator
 * Orchestrator accepts both BCP47 (fa-AF) and legacy (dari) formats
 */
function normalizeLanguage(language: Language | LanguageCode): string {
  // If it's already a string (BCP47 or legacy), pass through
  if (typeof language === 'string') {
    return language;
  }
  // Convert Language enum to string
  return String(language);
}

// =============================================================================
// AGENT 1: The Journalist (Writer/Fixer)
// =============================================================================
export async function runJournalistAgent(
  headline: string,
  deck: string,
  body: string,
  language: Language | LanguageCode,
  feedback: string[] = []
): Promise<JournalistResponse | AgentError> {
  const langStr = normalizeLanguage(language);

  console.log(`[runJournalistAgent] language: ${langStr}, feedback items: ${feedback.length}`);

  const result = await callExecuteAgent<JournalistResponse>('journalist', {
    headline,
    deck,
    body,
    feedback: feedback.length > 0 ? feedback.join('\n') : '',
  }, langStr);

  return result;
}

// =============================================================================
// AGENT 1b: TV Journalist (Broadcast Script Writer)
// =============================================================================
/**
 * TV-specific journalist agent that outputs {slug, anchor_intro, script}
 * for broadcast. Writes conversational, short sentences optimized for
 * being read aloud.
 *
 * @param slug - Story identifier
 * @param anchorIntro - Current anchor introduction text
 * @param script - Current script/VO text
 * @param language - Content language
 * @param durationTarget - Target duration in seconds
 * @param wordTargets - Optional word count targets for anchor and script
 * @param durationBreakdown - Optional current duration breakdown
 * @param feedback - Optional feedback items for revision
 * @param storyContext - Optional story context for fact storage
 */
export async function runTvJournalistAgent(
  slug: string,
  anchorIntro: string,
  script: string,
  language: Language | LanguageCode,
  durationTarget: number,
  wordTargets?: TvWordTargets | null,
  durationBreakdown?: DurationBreakdown | null,
  feedback: string[] = [],
  storyContext?: StoryContext
): Promise<TvJournalistAgentResponse | AgentError> {
  const langStr = normalizeLanguage(language);

  console.log(`[runTvJournalistAgent] language: ${langStr}, duration: ${durationTarget}s, feedback items: ${feedback.length}${storyContext ? ', with story context' : ''}`);

  // Build input data with duration context
  const inputData: Record<string, unknown> = {
    slug,
    anchor_intro: anchorIntro,
    script,
    duration_target: durationTarget,
    feedback: feedback.length > 0 ? feedback.join('\n') : '',
  };

  // Add word targets if provided
  if (wordTargets) {
    inputData.word_target_anchor = wordTargets.anchorWords;
    inputData.word_target_script = wordTargets.scriptWords;
  }

  // Add current duration breakdown if provided
  if (durationBreakdown) {
    inputData.current_anchor_words = durationBreakdown.anchorWords;
    inputData.current_script_words = durationBreakdown.scriptWords;
    inputData.current_anchor_duration = durationBreakdown.anchorDuration;
    inputData.current_script_duration = durationBreakdown.scriptDuration;
    inputData.current_total_duration = durationBreakdown.totalDuration;

    // Calculate over-target info for the prompt
    if (wordTargets) {
      const wordsOver = durationBreakdown.scriptWords - wordTargets.scriptWords;
      if (wordsOver > 0) {
        inputData.script_words_over = wordsOver;
        inputData.script_seconds_over = durationBreakdown.scriptDuration - durationBreakdown.scriptTarget;
      }
    }
  }

  const result = await callExecuteAgent<TvJournalistAgentResponse>('journalist_tv', inputData, langStr, storyContext);

  return result;
}

// =============================================================================
// AGENT 2: The Critic (Reviewer)
// =============================================================================
export async function runCriticAgent(
  headline: string,
  deck: string,
  body: string,
  language: Language | LanguageCode,
  isTranslation: boolean = false
): Promise<CriticResponse | AgentError> {
  const langStr = normalizeLanguage(language);

  // Use different agent based on whether it's a translation review
  const agentName = isTranslation ? 'critic_translation' : 'critic';

  console.log(`[runCriticAgent] agent: ${agentName}, language: ${langStr}, isTranslation: ${isTranslation}`);

  const result = await callExecuteAgent<CriticResponse>(agentName, {
    headline,
    deck,
    body,
  }, langStr);

  return result;
}

// =============================================================================
// AGENT 2b: Style Critic (Phase 2 - DeepSeek)
// Checks grammar, demonyms, broadcast format, terminology compliance
// =============================================================================

export interface StyleCriticIssue {
  category: 'ai_fixable' | 'human_required';
  type: 'grammar' | 'demonym' | 'format' | 'terminology' | 'style';
  text: string;
  location: 'headline' | 'deck' | 'body';
  original: string;
  suggestion?: string;
}

export interface StyleCriticResponse {
  issues: StyleCriticIssue[];
}

export async function runStyleCriticAgent(
  headline: string,
  deck: string,
  body: string,
  language: Language | LanguageCode
): Promise<StyleCriticResponse | AgentError> {
  const langStr = normalizeLanguage(language);

  console.log(`[runStyleCriticAgent] language: ${langStr}`);

  const result = await callExecuteAgent<StyleCriticResponse>('style_critic', {
    headline,
    deck,
    body,
  }, langStr);

  return result;
}

// =============================================================================
// AGENT 2c: Journalism Critic (Phase 2b - DeepSeek R1)
// Checks editorial quality: attribution, sourcing, logic, completeness, balance
// =============================================================================

export interface JournalismCriticIssue {
  category: 'ai_fixable' | 'human_required';
  type: 'attribution' | 'sourcing' | 'logic' | 'completeness' | 'balance';
  text: string;
  location: 'headline' | 'deck' | 'body';
  original?: string;
  suggestion?: string;
}

export interface JournalismCriticResponse {
  issues: JournalismCriticIssue[];
}

export async function runJournalismCriticAgent(
  headline: string,
  deck: string,
  body: string,
  language: Language | LanguageCode
): Promise<JournalismCriticResponse | AgentError> {
  const langStr = normalizeLanguage(language);

  console.log(`[runJournalismCriticAgent] language: ${langStr}`);

  const result = await callExecuteAgent<JournalismCriticResponse>('journalism_critic', {
    headline,
    deck,
    body,
  }, langStr);

  return result;
}

// =============================================================================
// AGENT 2d: Fact Checker (Phase 2c - Perplexity Sonar Pro)
// Verifies background facts using web search
// =============================================================================

export interface FactCheckIssue {
  category: 'ai_fixable' | 'human_required';
  type: 'fact_check';
  text: string;
  location: 'headline' | 'deck' | 'body';
  original?: string;
  suggestion?: string;
  confidence: 'verified' | 'unverified' | 'uncertain';
}

export interface FactCheckResponse {
  issues: FactCheckIssue[];
  fact_updates?: FactUpdate[];
  facts_updated?: boolean;
  facts_update_count?: number;
  facts_update_error?: string;
}

/**
 * Fact confidence update from critics.
 * Returned when critics verify facts from the story_facts table.
 */
export interface FactUpdate {
  fact_id: string;
  new_confidence: 'verified' | 'unverified' | 'disputed';
  confidence_note?: string;
  sources?: string[];  // URLs, deep_check only
}

/**
 * Input format for passing facts to verification critics.
 */
export interface FactForVerification {
  id: string;
  content: string;
  fact_type: string;
  source_attribution?: string;
  confidence: string;
}

export async function runFactCheckAgent(
  headline: string,
  deck: string,
  body: string,
  language: Language | LanguageCode,
  facts?: FactForVerification[]
): Promise<FactCheckResponse | AgentError> {
  const langStr = normalizeLanguage(language);

  console.log(`[runFactCheckAgent] language: ${langStr}, facts to verify: ${facts?.length || 0}`);

  const result = await callExecuteAgent<FactCheckResponse>('fact_checker', {
    headline,
    deck,
    body,
    facts: facts || [],
  }, langStr);

  return result;
}

// =============================================================================
// AGENT 2e: Deep Check (Phase 2d - Tavily + DeepSeek R1)
// Two-step deep verification: Tavily search + DeepSeek R1 analysis
// Searches international sources with Afghan coverage
// =============================================================================

export interface DeepCheckIssue {
  category: 'ai_fixable' | 'human_required';
  type: 'fact_check';
  text: string;
  location: 'headline' | 'deck' | 'body';
  original?: string;
  suggestion?: string;
  confidence: 'verified' | 'unverified' | 'uncertain';
  sources?: string[];  // URLs from Tavily search results
}

export interface DeepCheckResponse {
  issues: DeepCheckIssue[];
  fact_updates?: FactUpdate[];
  facts_updated?: boolean;
  facts_update_count?: number;
  facts_update_error?: string;
}

export async function runDeepCheckAgent(
  headline: string,
  deck: string,
  body: string,
  language: Language | LanguageCode,
  facts?: FactForVerification[]
): Promise<DeepCheckResponse | AgentError> {
  const langStr = normalizeLanguage(language);

  console.log(`[runDeepCheckAgent] language: ${langStr}, facts to verify: ${facts?.length || 0}`);

  const result = await callExecuteAgent<DeepCheckResponse>('deep_check', {
    headline,
    deck,
    body,
    facts: facts || [],
  }, langStr);

  return result;
}

// =============================================================================
// AGENT 2f: Curator (Phase 2e - DeepSeek V3)
// Consolidates and prioritizes issues from all review agents
// Merges duplicates and returns top 10 most important issues
// =============================================================================

export interface CuratorIssue {
  id: string;
  category: 'ai_fixable' | 'human_required';
  type: string;
  text: string;
  location?: 'headline' | 'deck' | 'body';
  original?: string;
  suggestion?: string;
  source: 'style_critic' | 'journalism_critic' | 'fact_checker' | 'deep_check';
  confidence?: 'verified' | 'unverified' | 'uncertain';
  sources?: string[];
  priority: 'critical' | 'high' | 'medium' | 'low';
}

export interface CuratorResponse {
  issues: CuratorIssue[];
  merged_count: number;
  reconciled_count?: number;
  total_input_count: number;
}

export async function runCuratorAgent(
  issues: unknown[],
  language: Language | LanguageCode
): Promise<CuratorResponse | AgentError> {
  const langStr = normalizeLanguage(language);

  console.log(`[runCuratorAgent] language: ${langStr}, input issues: ${issues.length}`);

  const result = await callExecuteAgent<CuratorResponse>('curator', {
    issues,
  }, langStr);

  return result;
}

export interface FactConsensusPromotionResponse {
  promoted_count: number;
  candidate_count: number;
  eligible_count: number;
  skipped_reason?: string;
  update_error?: string;
}

export interface FactConsensusPromotionGates {
  factCheckerSuccess: boolean;
  deepCheckSuccess: boolean;
  factCheckerParsed: boolean;
  deepCheckParsed: boolean;
}

export async function runFactConsensusPromotion(
  storyContext: StoryContext,
  language: Language | LanguageCode,
  candidateFactIds: string[],
  gates: FactConsensusPromotionGates
): Promise<FactConsensusPromotionResponse | AgentError> {
  const langStr = normalizeLanguage(language);

  console.log(
    `[runFactConsensusPromotion] story: ${storyContext.storyId}, candidates: ${candidateFactIds.length}, gates: fc=${gates.factCheckerSuccess}/${gates.factCheckerParsed}, dc=${gates.deepCheckSuccess}/${gates.deepCheckParsed}`
  );

  const result = await callExecuteAgent<FactConsensusPromotionResponse>(
    'fact_consensus_promotion',
    {
      candidate_fact_ids: candidateFactIds,
      fact_checker_success: gates.factCheckerSuccess,
      deep_check_success: gates.deepCheckSuccess,
      fact_checker_parsed: gates.factCheckerParsed,
      deep_check_parsed: gates.deepCheckParsed,
    },
    langStr,
    storyContext
  );

  return result;
}

// =============================================================================
// PARALLEL CRITICS: Run all 4 critics simultaneously
// Style, Journalism, Fact Check, Deep Check - then Curator consolidates
// =============================================================================

/**
 * Result from a single critic in the parallel run
 */
export interface CriticResult {
  success: boolean;
  issues: EditorIssue[];
  responseParsed?: boolean;
  error?: string;
  // Fact confidence updates (fact_checker, deep_check only)
  factsUpdated?: boolean;
  factsUpdateCount?: number;
  factsUpdateError?: string;
}

/**
 * Combined result from running all 4 critics in parallel
 */
export interface ParallelCriticsResult {
  style: CriticResult;
  journalism: CriticResult;
  factCheck: CriticResult;
  deepCheck: CriticResult;
  duration_ms: number;
}

/**
 * Helper to check if a response is an AgentError
 */
function isAgentError(response: unknown): response is AgentError {
  if (typeof response === 'string') return false;
  if (response && typeof response === 'object' && 'error' in response) {
    return (response as AgentError).error === true;
  }
  return false;
}

/**
 * Helper to safely parse agent response (handles string or object)
 */
function parseAgentResponse<T>(response: unknown): T | null {
  if (typeof response === 'string') {
    try {
      const parsed = JSON.parse(response) as T;
      console.log('[parseAgentResponse] Successfully parsed string to JSON');
      return parsed;
    } catch (e) {
      console.warn('[parseAgentResponse] Failed to parse string response. Error:', e);
      console.warn('[parseAgentResponse] String content (first 500 chars):', response.slice(0, 500));
      return null;
    }
  }
  return response as T;
}

/**
 * Run critics based on review mode.
 * - 'full': All 4 critics (style, journalism, fact-check, deep-check) in parallel
 * - 'light': Only style critic (for derivatives - web articles, translations)
 *
 * @param headline - Story headline (or empty for TV content)
 * @param deck - Story deck/summary (or empty for TV content)
 * @param body - Story body (or empty for TV content)
 * @param language - Content language
 * @param reviewMode - 'full' for canonical content, 'light' for derivatives (default: 'full')
 */
export async function runParallelCritics(
  headline: string,
  deck: string,
  body: string,
  language: Language | LanguageCode,
  reviewMode: ReviewMode = 'full',
  facts?: FactForVerification[]
): Promise<ParallelCriticsResult> {
  const startTime = Date.now();
  const langStr = normalizeLanguage(language);

  // LIGHT MODE: Only run style critic (for derivatives)
  if (reviewMode === 'light') {
    console.log(`[runParallelCritics] LIGHT MODE: Running style critic only, language: ${langStr}`);

    const styleResult = await runStyleCriticAgent(headline, deck, body, language)
      .then(r => ({ status: 'fulfilled' as const, value: r }))
      .catch(e => ({ status: 'rejected' as const, reason: e }));

    const duration_ms = Date.now() - startTime;
    const style = processStyleCriticResult(styleResult as PromiseSettledResult<StyleCriticResponse | AgentError>);

    console.log(`[runParallelCritics] LIGHT MODE completed in ${duration_ms}ms, style issues: ${style.success ? style.issues.length : 'failed'}`);

    return {
      style,
      journalism: { success: true, issues: [], responseParsed: true },  // Skipped in light mode
      factCheck: { success: true, issues: [], responseParsed: true },   // Skipped in light mode
      deepCheck: { success: true, issues: [], responseParsed: true },   // Skipped in light mode
      duration_ms,
    };
  }

  // FULL MODE: Run all 4 critics in parallel
  console.log(`[runParallelCritics] FULL MODE: Starting 4 critics in parallel, language: ${langStr}, facts: ${facts?.length || 0}`);

  const [styleResult, journalismResult, factCheckResult, deepCheckResult] =
    await Promise.allSettled([
      runStyleCriticAgent(headline, deck, body, language),
      runJournalismCriticAgent(headline, deck, body, language),
      runFactCheckAgent(headline, deck, body, language, facts),
      runDeepCheckAgent(headline, deck, body, language, facts),
    ]);

  const duration_ms = Date.now() - startTime;
  console.log(`[runParallelCritics] FULL MODE completed in ${duration_ms}ms`);

  // Process Style Critic result
  const style: CriticResult = processStyleCriticResult(styleResult);

  // Process Journalism Critic result
  const journalism: CriticResult = processJournalismCriticResult(journalismResult);

  // Process Fact Check result
  const factCheck: CriticResult = processFactCheckResult(factCheckResult);

  // Process Deep Check result
  const deepCheck: CriticResult = processDeepCheckResult(deepCheckResult);

  // Log summary
  console.log(`[runParallelCritics] Results: style=${style.success ? style.issues.length : 'failed'}, journalism=${journalism.success ? journalism.issues.length : 'failed'}, factCheck=${factCheck.success ? factCheck.issues.length : 'failed'}, deepCheck=${deepCheck.success ? deepCheck.issues.length : 'failed'}`);

  return {
    style,
    journalism,
    factCheck,
    deepCheck,
    duration_ms,
  };
}

/**
 * Process Style Critic settled promise result
 */
function processStyleCriticResult(
  result: PromiseSettledResult<StyleCriticResponse | AgentError>
): CriticResult {
  if (result.status === 'rejected') {
    console.error('[StyleCritic] Promise rejected:', result.reason);
    return { success: false, issues: [], responseParsed: false, error: String(result.reason) };
  }

  const response: unknown = result.value;

  // Log raw response for debugging
  if (typeof response === 'string') {
    console.log('[StyleCritic] Raw string response (first 300 chars):', response.slice(0, 300));
  }

  if (isAgentError(response)) {
    console.error('[StyleCritic] Agent error:', response.message);
    return { success: false, issues: [], responseParsed: false, error: response.message };
  }

  const parsed = parseAgentResponse<StyleCriticResponse>(response);
  if (!parsed?.issues) {
    console.warn('[StyleCritic] No issues found in parsed response');
    return { success: true, issues: [], responseParsed: false };
  }

  console.log(`[StyleCritic] Successfully parsed ${parsed.issues.length} issues`);

  const issues: EditorIssue[] = parsed.issues.map((issue, idx) => ({
    id: `style-${idx}`,
    category: issue.category || 'human_required',
    text: typeof issue === 'string' ? issue : issue.text,
    suggestion: issue.suggestion,
    type: issue.type,
    location: issue.location,
    original: issue.original,
    source: 'style_critic' as const,
    applied: false,
  }));

  return { success: true, issues, responseParsed: true };
}

/**
 * Process Journalism Critic settled promise result
 */
function processJournalismCriticResult(
  result: PromiseSettledResult<JournalismCriticResponse | AgentError>
): CriticResult {
  if (result.status === 'rejected') {
    console.error('[JournalismCritic] Promise rejected:', result.reason);
    return { success: false, issues: [], responseParsed: false, error: String(result.reason) };
  }

  const response: unknown = result.value;

  // Log raw response for debugging
  if (typeof response === 'string') {
    console.log('[JournalismCritic] Raw string response (first 300 chars):', response.slice(0, 300));
  }

  if (isAgentError(response)) {
    console.error('[JournalismCritic] Agent error:', response.message);
    return { success: false, issues: [], responseParsed: false, error: response.message };
  }

  const parsed = parseAgentResponse<JournalismCriticResponse>(response);
  if (!parsed?.issues) {
    console.warn('[JournalismCritic] No issues found in parsed response');
    return { success: true, issues: [], responseParsed: false };
  }

  console.log(`[JournalismCritic] Successfully parsed ${parsed.issues.length} issues`);

  const issues: EditorIssue[] = parsed.issues.map((issue, idx) => ({
    id: `journalism-${idx}`,
    category: issue.category || 'human_required',
    text: typeof issue === 'string' ? issue : issue.text,
    suggestion: issue.suggestion,
    type: issue.type,
    location: issue.location,
    original: issue.original,
    source: 'journalism_critic' as const,
    applied: false,
  }));

  return { success: true, issues, responseParsed: true };
}

/**
 * Process Fact Check settled promise result
 */
function processFactCheckResult(
  result: PromiseSettledResult<FactCheckResponse | AgentError>
): CriticResult {
  if (result.status === 'rejected') {
    console.error('[FactChecker] Promise rejected:', result.reason);
    return { success: false, issues: [], responseParsed: false, error: String(result.reason) };
  }

  const response: unknown = result.value;

  // Log raw response for debugging
  if (typeof response === 'string') {
    console.log('[FactChecker] Raw string response (first 300 chars):', response.slice(0, 300));
  }

  if (isAgentError(response)) {
    console.error('[FactChecker] Agent error:', response.message);
    return { success: false, issues: [], responseParsed: false, error: response.message };
  }

  const parsed = parseAgentResponse<FactCheckResponse>(response);
  if (!parsed || !Array.isArray(parsed.issues)) {
    console.error('[FactChecker] Invalid response format - treating as failure');
    return { success: false, issues: [], responseParsed: false, error: 'Invalid fact checker response format' };
  }

  console.log(`[FactChecker] Successfully parsed ${parsed.issues.length} issues`);

  const issues: EditorIssue[] = parsed.issues.map((issue, idx) => ({
    id: `fact-${idx}`,
    category: issue.category || 'human_required',
    text: typeof issue === 'string' ? issue : issue.text,
    suggestion: issue.suggestion,
    type: 'fact_check' as const,
    location: issue.location,
    original: issue.original,
    source: 'fact_checker' as const,
    confidence: issue.confidence,
    applied: false,
  }));

  // Extract fact update info if present
  const factsUpdated = parsed.facts_updated;
  const factsUpdateCount = parsed.facts_update_count;
  const factsUpdateError = parsed.facts_update_error;

  if (factsUpdateCount) {
    console.log(`[FactChecker] Updated ${factsUpdateCount} fact confidences`);
  }

  return {
    success: true,
    responseParsed: true,
    issues,
    factsUpdated,
    factsUpdateCount,
    factsUpdateError,
  };
}

/**
 * Process Deep Check settled promise result
 */
function processDeepCheckResult(
  result: PromiseSettledResult<DeepCheckResponse | AgentError>
): CriticResult {
  if (result.status === 'rejected') {
    console.error('[DeepCheck] Promise rejected:', result.reason);
    return { success: false, issues: [], responseParsed: false, error: String(result.reason) };
  }

  const response: unknown = result.value;

  // Log the raw response for debugging
  console.log('[DeepCheck] Raw response type:', typeof response);
  if (typeof response === 'string') {
    console.log('[DeepCheck] Raw string response (first 500 chars):', response.slice(0, 500));
  } else {
    console.log('[DeepCheck] Raw object response:', JSON.stringify(response, null, 2).slice(0, 1000));
  }

  if (isAgentError(response)) {
    console.error('[DeepCheck] Agent error:', response.message);
    return { success: false, issues: [], responseParsed: false, error: response.message };
  }

  const parsed = parseAgentResponse<DeepCheckResponse>(response);
  if (!parsed || !Array.isArray(parsed.issues)) {
    console.error('[DeepCheck] Invalid response format - treating as failure. Parsed result:', parsed);
    return { success: false, issues: [], responseParsed: false, error: 'Invalid deep check response format' };
  }

  console.log(`[DeepCheck] Successfully parsed ${parsed.issues.length} issues`);

  const issues: EditorIssue[] = parsed.issues.map((issue, idx) => ({
    id: `deep-${idx}`,
    category: issue.category || 'human_required',
    text: typeof issue === 'string' ? issue : issue.text,
    suggestion: issue.suggestion,
    type: 'fact_check' as const,
    location: issue.location,
    original: issue.original,
    source: 'deep_check' as const,
    confidence: issue.confidence,
    sources: issue.sources,
    applied: false,
  }));

  // Extract fact update info if present
  const factsUpdated = parsed.facts_updated;
  const factsUpdateCount = parsed.facts_update_count;
  const factsUpdateError = parsed.facts_update_error;

  if (factsUpdateCount) {
    console.log(`[DeepCheck] Updated ${factsUpdateCount} fact confidences`);
  }

  return {
    success: true,
    responseParsed: true,
    issues,
    factsUpdated,
    factsUpdateCount,
    factsUpdateError,
  };
}

// =============================================================================
// AGENT 3: Social Media Generator
// =============================================================================
export async function runSocialAgent(
  headline: string,
  body: string,
  language: Language | LanguageCode = Language.ENGLISH
): Promise<SocialPosts | AgentError> {
  const langStr = normalizeLanguage(language);

  console.log(`[runSocialAgent] language: ${langStr}`);

  const result = await callExecuteAgent<SocialPosts>('social', {
    headline,
    body,
  }, langStr);

  return result;
}

// =============================================================================
// AGENT 4: Translator (legacy - web articles only)
// =============================================================================
export async function runTranslatorAgent(
  headline: string,
  deck: string,
  body: string,
  sourceLang: Language | LanguageCode,
  targetLang: Language | LanguageCode
): Promise<JournalistResponse | AgentError> {
  const sourceLangStr = normalizeLanguage(sourceLang);
  const targetLangStr = normalizeLanguage(targetLang);

  console.log(`[runTranslatorAgent] ${sourceLangStr} -> ${targetLangStr}`);

  // Pass targetLang as the main language for terminology lookup
  // (terminology rules should apply to the OUTPUT language)
  const result = await callExecuteAgent<JournalistResponse>('translator', {
    headline,
    deck,
    body,
    source_language: sourceLangStr,
    target_language: targetLangStr,
  }, targetLangStr);

  return result;
}

// =============================================================================
// PHASE 3: WEB REWRITER AGENT (TV -> Web conversion)
// =============================================================================

import { WebRewriterResponse, TranslatorResponse } from '../types';

/**
 * Converts an approved TV script to a web article.
 * Input: TV format (slug, anchor_intro, script)
 * Output: Web format (headline, deck, body)
 *
 * @param groupId - Optional story group ID to load facts from Facts Stream.
 *                  When provided, facts are loaded from the database and used
 *                  to create a richer web article with information that may not
 *                  have fit in the compressed TV script.
 */
export async function runWebRewriterAgent(
  slug: string,
  anchorIntro: string,
  script: string,
  language: Language | LanguageCode,
  groupId?: string
): Promise<WebRewriterResponse | AgentError> {
  const langStr = normalizeLanguage(language);

  console.log(`[runWebRewriterAgent] language: ${langStr}, slug: ${slug}${groupId ? `, groupId: ${groupId}` : ''}`);

  const result = await callExecuteAgent<WebRewriterResponse>('web_rewriter', {
    slug,
    anchor_intro: anchorIntro,
    script,
  }, langStr, groupId ? { storyId: '', groupId } : undefined);

  return result;
}

// =============================================================================
// PHASE 3: TRANSLATOR V2 (handles both TV and web formats)
// =============================================================================

/**
 * Enhanced translator that handles both TV scripts and web articles.
 * Returns the same format as input (preserves structure).
 */
export async function runTranslatorAgentV2(
  content: {
    headline?: string;
    deck?: string;
    body?: string;
    slug?: string;
    anchorIntro?: string;
    script?: string;
  },
  sourceLanguage: Language | LanguageCode,
  targetLanguage: Language | LanguageCode
): Promise<TranslatorResponse | AgentError> {
  const sourceLangStr = normalizeLanguage(sourceLanguage);
  const targetLangStr = normalizeLanguage(targetLanguage);

  console.log(`[runTranslatorAgentV2] ${sourceLangStr} -> ${targetLangStr}`);

  const result = await callExecuteAgent<TranslatorResponse>('translator', {
    headline: content.headline || '',
    deck: content.deck || '',
    body: content.body || '',
    slug: content.slug || '',
    anchor_intro: content.anchorIntro || '',
    script: content.script || '',
    source_language: sourceLangStr,
    target_language: targetLangStr,
  }, targetLangStr);

  return result;
}

// =============================================================================
// PHASE 3: SOCIAL GENERATOR (handles both TV and web input)
// =============================================================================

/**
 * Enhanced social generator that accepts both TV and web content.
 * Generates posts for Twitter, Facebook, and Telegram.
 *
 * IMPORTANT: If groupId is provided, the social generator will use ONLY
 * verified facts from the Facts Stream. This ensures social posts
 * never include disputed or unverified claims.
 *
 * @param content - Story content (TV or web fields)
 * @param language - Content language
 * @param groupId - Optional story group ID to load verified facts
 */
export async function runSocialGeneratorAgent(
  content: {
    headline?: string;
    deck?: string;
    body?: string;
    slug?: string;
    anchorIntro?: string;
    script?: string;
  },
  language: Language | LanguageCode,
  groupId?: string
): Promise<SocialPosts | AgentError> {
  const langStr = normalizeLanguage(language);

  console.log(`[runSocialGeneratorAgent] language: ${langStr}${groupId ? `, groupId: ${groupId}` : ''}`);

  // Create story context if groupId is provided
  // Note: storyId is not needed for social (we don't store facts back)
  const storyContext = groupId ? { storyId: '', groupId } : undefined;

  const result = await callExecuteAgent<SocialPosts>('social', {
    headline: content.headline || '',
    deck: content.deck || '',
    body: content.body || '',
    slug: content.slug || '',
    anchor_intro: content.anchorIntro || '',
    script: content.script || '',
  }, langStr, storyContext);

  return result;
}

// =============================================================================
// Headline Suggestion (uses legacy endpoint - not yet in orchestrator)
// =============================================================================
export async function suggestHeadline(
  body: string,
  language: Language
): Promise<{ headline: string; deck: string }> {
  const result = await callEdgeFunctionLegacy<{ headline: string; deck: string }>('suggest-headline', {
    body,
    language
  });

  // Handle error case
  if (isAgentError(result)) {
    console.error('[suggestHeadline] Error:', result.message);
    return { headline: '', deck: '' };
  }

  return result || { headline: '', deck: '' };
}

// =============================================================================
// Legacy helper (still works, routes through journalist agent)
// =============================================================================
export async function polishStory(body: string): Promise<string> {
  const res = await runJournalistAgent('', '', body, Language.ENGLISH, []);

  // Handle error case
  if (isAgentError(res)) {
    console.error('[polishStory] Error:', res.message);
    return body;
  }

  return res.body || body;
}

// =============================================================================
// DUAL POLISH: Run journalist + validator in parallel
// =============================================================================
export async function runDualPolishAgent(
  headline: string,
  deck: string,
  body: string,
  language: Language | LanguageCode,
  feedback: string[] = []
): Promise<DualPolishResult | AgentError> {
  const langStr = normalizeLanguage(language);

  console.log(`[runDualPolishAgent] language: ${langStr}, dual mode enabled`);

  try {
    // Get valid session
    const sessionResult = await getValidSession();
    if ('error' in sessionResult) {
      return sessionResult;
    }
    const { session } = sessionResult;

    const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!SUPABASE_ANON_KEY) {
      return {
        error: true,
        message: 'Configuration error: Missing API key',
        code: 'CONFIG_ERROR'
      };
    }

    const response = await fetch(`${SUPABASE_URL}/functions/v1/execute-agent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        agent_name: 'journalist',
        validation_agent_name: 'journalist_validator',
        input_data: {
          headline,
          deck,
          body,
          feedback: feedback.length > 0 ? feedback.join('\n') : '',
        },
        language: langStr,
      }),
    });

    if (!response.ok) {
      let errorText = '';
      try {
        errorText = await response.text();
      } catch (e) {
        errorText = `HTTP ${response.status}`;
      }

      return {
        error: true,
        message: `Server error: HTTP ${response.status}`,
        details: errorText.slice(0, 500),
        code: response.status
      };
    }

    const result: DualAgentResponse<JournalistAgentResponse> = await response.json();

    console.log(`[runDualPolishAgent] Duration: ${result.duration_ms}ms`);
    console.log(`[runDualPolishAgent] Primary (${result.primary.model}): ${result.primary.success ? 'success' : 'failed'}`);
    console.log(`[runDualPolishAgent] Validator (${result.validator.model}): ${result.validator.success ? 'success' : 'failed'}`);

    // Transform to DualPolishResult
    const primaryData = result.primary.data as JournalistAgentResponse | undefined;
    const validatorData = result.validator.data as JournalistAgentResponse | undefined;

    // Use backend semantic agreement (embeddings), fallback to fuzzy matching if not available
    let agreementDetails: { overall: number; headline: number; deck: number; body: number };

    if (result.agreement) {
      // Use semantic agreement from backend (embeddings)
      agreementDetails = result.agreement;
      console.log(`[runDualPolishAgent] Using semantic agreement: ${agreementDetails.overall}%`);
    } else {
      // Fallback to local fuzzy matching (for backwards compatibility or if embeddings failed)
      console.warn(`[runDualPolishAgent] No backend agreement, using fuzzy fallback`);
      const { calculateAgreementDetailed } = await import('../utils/textSimilarity');
      agreementDetails = calculateAgreementDetailed(
        { headline: primaryData?.headline, deck: primaryData?.deck, body: primaryData?.body },
        { headline: validatorData?.headline, deck: validatorData?.deck, body: validatorData?.body }
      );
    }

    console.log(`[runDualPolishAgent] Agreement: ${agreementDetails.overall}% (headline: ${agreementDetails.headline}%, deck: ${agreementDetails.deck}%, body: ${agreementDetails.body}%)`);

    return {
      primary: {
        success: result.primary.success,
        headline: primaryData?.headline,
        deck: primaryData?.deck,
        body: primaryData?.body,
        error: result.primary.error,
        model: result.primary.model,
      },
      validator: {
        success: result.validator.success,
        headline: validatorData?.headline,
        deck: validatorData?.deck,
        body: validatorData?.body,
        error: result.validator.error,
        model: result.validator.model,
      },
      agreement: {
        overall: agreementDetails.overall,
        headline: agreementDetails.headline,
        deck: agreementDetails.deck,
        body: agreementDetails.body,
        isHighConfidence: agreementDetails.overall >= 90,
      },
      duration_ms: result.duration_ms,
    };

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error(`[runDualPolishAgent] Network error:`, error);

    return {
      error: true,
      message: `Network error: ${errorMessage}`,
      details: error instanceof Error ? error.stack : String(error),
      code: 'NETWORK_ERROR'
    };
  }
}
